/**
 * usePermissions — React hook for RBAC permission checks.
 *
 * Reads the current user's role from Zustand and resolves their permission set
 * from the static ROLE_PERMISSIONS map. Works fully offline.
 *
 * Usage:
 *   const { can, canAny, canAll, permissions, role } = usePermissions();
 *
 *   if (can('billing:create')) { ... }
 *   if (canAny(['reports:view', 'reports:gst'])) { ... }
 */

import { useMemo } from 'react';
import { useAppStore } from '../store/useAppStore';
import { type Permission } from './permissions';
import { getPermissionsForRole, hasPermission, hasAnyPermission, hasAllPermissions } from './roles';

export interface PermissionContext {
  /** Current user's role string (e.g. 'OWNER', 'CASHIER'). Empty string if not authenticated. */
  role: string;
  /** Full resolved permission set for the current role */
  permissions: ReadonlySet<Permission>;
  /** Check a single permission: `can('billing:create')` */
  can: (permission: Permission) => boolean;
  /** Check if ANY permission is granted (OR): `canAny(['billing:view', 'billing:create'])` */
  canAny: (permissions: Permission[]) => boolean;
  /** Check if ALL permissions are granted (AND): `canAll(['inventory:view', 'inventory:edit'])` */
  canAll: (permissions: Permission[]) => boolean;
}

/**
 * React hook — use inside components behind the auth gate.
 * Re-renders only when the user's role changes.
 */
export function usePermissions(): PermissionContext {
  const role = useAppStore((s) => s.currentUser?.role ?? '');

  return useMemo(() => {
    const permissions = getPermissionsForRole(role);
    return {
      role,
      permissions,
      can: (permission: Permission) => hasPermission(role, permission),
      canAny: (perms: Permission[]) => hasAnyPermission(role, perms),
      canAll: (perms: Permission[]) => hasAllPermissions(role, perms),
    };
  }, [role]);
}
